import { apiClient } from '@/lib/api/client'
import axios from 'axios'
import type { ChildTicketTask } from '@/lib/services/childTicketService'

function apiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message
    if (typeof message === 'string' && message.trim() !== '') {
      if (/No query results for model \[App\\Models\\Support\\ChildTicket\]/i.test(message)) {
        return 'This task no longer exists (it may have been removed or the workflow was reset). Refresh the Tasks tab.'
      }
      return message
    }
    if (error.response?.status === 404) {
      return 'This task no longer exists. Refresh the Tasks tab.'
    }
  }
  if (error instanceof Error && error.message) {
    if (/No query results for model \[App\\Models\\Support\\ChildTicket\]/i.test(error.message)) {
      return 'This task no longer exists (it may have been removed or the workflow was reset). Refresh the Tasks tab.'
    }
    return error.message
  }
  return fallback
}

type ApiResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

export type TaskAttachment = {
  id: number
  task_id: number
  original_filename: string
  mime_type?: string | null
  size_bytes?: number | null
  uploaded_by?: number | null
  uploader_name?: string | null
  created_at?: string | null
}

export type TaskActivity = {
  id: number
  task_id: number
  module_code: string
  event_code: string
  payload?: Record<string, unknown> | null
  created_by?: number | null
  creator_name?: string | null
  created_at: string
}

export type TaskUpdate = {
  id: number
  child_ticket_id?: number | null
  parent_ticket_id?: number | null
  user_id?: number | null
  content: string
  type: 'comment' | 'status_change' | 'note'
  is_internal: boolean
  author?: {
    id: number
    first_name?: string | null
    last_name?: string | null
    user_login?: string | null
    full_name: string
  } | null
  created_at: string
  updated_at?: string | null
}

export type TaskPatchPayload = {
  title?: string
  status?: string
  priority?: string | null
  due_at?: string | null
  assigned_to?: number | null
  form_data?: Record<string, unknown>
  notes?: string
}

export type TaskSummary = {
  total: number
  by_status: Record<string, number>
  open_count: number
  completed_count: number
}

export type TaskUpdateFormValues = {
  content: string
  type?: 'comment' | 'status_change' | 'note'
  is_internal?: boolean
}

export type FeVisitEvent = {
  id: number
  child_ticket_id: number
  event_type: string
  actor_id?: number | null
  actor_name?: string | null
  payload?: Record<string, unknown> | null
  latitude?: number | null
  longitude?: number | null
  occurred_at: string
}

export type FeVisitEventsResult = {
  events: FeVisitEvent[]
  endpointMissing: boolean
}

export type TaskChecklistItem = {
  id: number
  task_id: number
  template_item_id?: number | null
  label: string
  checked: boolean
  checked_by?: number | null
  checked_at?: string | null
  note?: string | null
  required: boolean
}

export type TaskChecklistProgress = {
  total: number
  checked: number
  required_total: number
  required_checked: number
}

export type TaskChecklistResult = {
  items: TaskChecklistItem[]
  progress: TaskChecklistProgress
}

export type TaskDependencySummary = {
  id: number
  task_number?: string | null
  title?: string | null
  status?: string | null
  actual_end?: string | null
  is_complete: boolean
}

export type TaskDependenciesResult = {
  blocked_by: TaskDependencySummary[]
  blocking: TaskDependencySummary[]
}

export type FeExpense = {
  id: number
  child_ticket_id: number
  fe_user_id: number
  category: string
  amount: number
  currency?: string | null
  status: string
  reviewed_by?: number | null
  reviewed_at?: string | null
  created_at?: string | null
}

function unwrapData<T>(res: { data?: ApiResponse<T> | T }): T {
  const body = res.data
  if (body && typeof body === 'object' && 'data' in body && (body as ApiResponse<T>).data !== undefined) {
    return (body as ApiResponse<T>).data as T
  }
  return body as T
}

function mapAttachment(row: Record<string, unknown>): TaskAttachment {
  return {
    id: Number(row.id),
    task_id: Number(row.task_id ?? row.taskId),
    original_filename: String(row.original_filename ?? row.originalFilename ?? row.file_name ?? ''),
    mime_type: (row.mime_type ?? row.mimeType) as string | null | undefined,
    size_bytes:
      row.size_bytes != null
        ? Number(row.size_bytes)
        : row.sizeBytes != null
          ? Number(row.sizeBytes)
          : row.file_size != null
            ? Number(row.file_size)
            : null,
    uploaded_by:
      row.uploaded_by != null
        ? Number(row.uploaded_by)
        : row.uploadedBy != null
          ? Number(row.uploadedBy)
          : null,
    uploader_name: (row.uploader_name ?? row.uploaderName) as string | null | undefined,
    created_at: (row.created_at ?? row.createdAt) as string | null | undefined,
  }
}

function mapActivity(row: Record<string, unknown>): TaskActivity {
  return {
    id: Number(row.id),
    task_id: Number(row.task_id ?? row.taskId),
    module_code: String(row.module_code ?? row.moduleCode ?? ''),
    event_code: String(row.event_code ?? row.eventCode ?? ''),
    payload: (row.payload as Record<string, unknown> | null | undefined) ?? null,
    created_by:
      row.created_by != null
        ? Number(row.created_by)
        : row.createdBy != null
          ? Number(row.createdBy)
          : null,
    creator_name: (row.creator_name ?? row.creatorName) as string | null | undefined,
    created_at: String(row.created_at ?? row.createdAt ?? ''),
  }
}

function mapVisitEvent(row: Record<string, unknown>): FeVisitEvent {
  return {
    id: Number(row.id),
    child_ticket_id: Number(row.child_ticket_id ?? row.childTicketId ?? row.task_id ?? row.taskId),
    event_type: String(row.event_type ?? row.eventType ?? ''),
    actor_id:
      row.actor_id != null
        ? Number(row.actor_id)
        : row.actorId != null
          ? Number(row.actorId)
          : null,
    actor_name: (row.actor_name ?? row.actorName) as string | null | undefined,
    payload: (row.payload as Record<string, unknown> | null | undefined) ?? null,
    latitude:
      row.latitude != null
        ? Number(row.latitude)
        : row.lat != null
          ? Number(row.lat)
          : null,
    longitude:
      row.longitude != null
        ? Number(row.longitude)
        : row.lng != null
          ? Number(row.lng)
          : null,
    occurred_at: String(row.occurred_at ?? row.occurredAt ?? row.created_at ?? row.createdAt ?? ''),
  }
}

function mapUpdate(row: Record<string, unknown>): TaskUpdate {
  const authorRaw = row.author as Record<string, unknown> | null | undefined
  return {
    id: Number(row.id),
    child_ticket_id:
      row.child_ticket_id != null
        ? Number(row.child_ticket_id)
        : row.childTicketId != null
          ? Number(row.childTicketId)
          : null,
    parent_ticket_id:
      row.parent_ticket_id != null
        ? Number(row.parent_ticket_id)
        : row.parentTicketId != null
          ? Number(row.parentTicketId)
          : null,
    user_id:
      row.user_id != null ? Number(row.user_id) : row.userId != null ? Number(row.userId) : null,
    content: String(row.content ?? ''),
    type: (row.type as TaskUpdate['type']) ?? 'comment',
    is_internal: Boolean(row.is_internal ?? row.isInternal),
    author: authorRaw
      ? {
          id: Number(authorRaw.id),
          first_name: authorRaw.first_name as string | null | undefined,
          last_name: authorRaw.last_name as string | null | undefined,
          user_login: authorRaw.user_login as string | null | undefined,
          full_name: String(authorRaw.full_name ?? authorRaw.fullName ?? 'Agent'),
        }
      : null,
    created_at: String(row.created_at ?? row.createdAt ?? ''),
    updated_at: (row.updated_at ?? row.updatedAt) as string | null | undefined,
  }
}

export const taskService = {
  async getTaskSummary(parentTicketId: string | number): Promise<TaskSummary> {
    try {
      const res = await apiClient.get<ApiResponse<TaskSummary>>(
        `/parent-tickets/${parentTicketId}/task-summary`,
      )
      const data = unwrapData<TaskSummary>(res)
      return {
        total: Number(data?.total ?? 0),
        by_status: data?.by_status ?? {},
        open_count: Number(data?.open_count ?? 0),
        completed_count: Number(data?.completed_count ?? 0),
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load task summary'))
    }
  },

  async show(taskId: number): Promise<ChildTicketTask> {
    try {
      const res = await apiClient.get<ApiResponse<ChildTicketTask>>(`/tasks/${taskId}`)
      const data = unwrapData<ChildTicketTask>(res)
      if (!data?.id) {
        throw new Error('Task not found')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load task'))
    }
  },

  async update(taskId: number, payload: TaskPatchPayload): Promise<ChildTicketTask> {
    try {
      const res = await apiClient.patch<ApiResponse<ChildTicketTask>>(`/tasks/${taskId}`, payload)
      const data = unwrapData<ChildTicketTask>(res)
      if (!data?.id) {
        throw new Error('Failed to update task')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to update task'))
    }
  },

  async assign(taskId: number, assignedTo: number | null): Promise<ChildTicketTask> {
    try {
      const res = await apiClient.patch<ApiResponse<ChildTicketTask>>(`/tasks/${taskId}/assign`, {
        assigned_to: assignedTo,
      })
      const data = unwrapData<ChildTicketTask>(res)
      if (!data?.id) {
        throw new Error('Failed to assign task')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to assign task'))
    }
  },

  async addWatcher(taskId: number, userId: number): Promise<{ task_id: number; user_id: number; created_at?: string | null }> {
    try {
      const res = await apiClient.post<
        ApiResponse<{ task_id: number; user_id: number; created_at?: string | null }>
      >(`/tasks/${taskId}/watchers`, { user_id: userId })
      const data = unwrapData<{ task_id: number; user_id: number; created_at?: string | null }>(res)
      if (!data?.user_id) {
        throw new Error('Failed to add watcher')
      }
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to add watcher'))
    }
  },

  async removeWatcher(taskId: number, userId: number): Promise<void> {
    try {
      await apiClient.delete(`/tasks/${taskId}/watchers/${userId}`)
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to remove watcher'))
    }
  },

  async listAttachments(taskId: number): Promise<TaskAttachment[]> {
    try {
      const res = await apiClient.get<ApiResponse<TaskAttachment[]>>(`/tasks/${taskId}/attachments`)
      const data = unwrapData<TaskAttachment[] | Record<string, unknown>[]>(res)
      if (!Array.isArray(data)) return []
      return data.map((row) => mapAttachment(row as Record<string, unknown>))
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load attachments'))
    }
  },

  async uploadAttachment(taskId: number, file: File): Promise<TaskAttachment> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await apiClient.post<ApiResponse<TaskAttachment>>(`/tasks/${taskId}/attachments`, formData)
      const data = unwrapData<TaskAttachment | Record<string, unknown>>(res)
      return mapAttachment(data as Record<string, unknown>)
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to upload attachment'))
    }
  },

  async deleteAttachment(taskId: number, attachmentId: number): Promise<void> {
    try {
      await apiClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`)
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to delete attachment'))
    }
  },

  getAttachmentFileUrl(taskId: number, attachmentId: number): string {
    const apiBaseUrl = apiClient.defaults.baseURL ?? ''
    return `${apiBaseUrl}/tasks/${taskId}/attachments/${attachmentId}/file`
  },

  async fetchAttachmentFile(taskId: number, attachmentId: number): Promise<Blob> {
    try {
      const res = await apiClient.get(this.getAttachmentFileUrl(taskId, attachmentId), {
        responseType: 'blob',
      })
      return res.data as Blob
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to download attachment'))
    }
  },

  async listActivities(taskId: number): Promise<TaskActivity[]> {
    try {
      const res = await apiClient.get<ApiResponse<TaskActivity[]>>(`/tasks/${taskId}/activities`)
      const data = unwrapData<TaskActivity[] | Record<string, unknown>[]>(res)
      if (!Array.isArray(data)) return []
      return data.map((row) => mapActivity(row as Record<string, unknown>))
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load activities'))
    }
  },

  async listUpdates(taskId: number, page = 1, perPage = 50): Promise<TaskUpdate[]> {
    try {
      const res = await apiClient.get<ApiResponse<{ data?: TaskUpdate[] } | TaskUpdate[]>>(
        `/tasks/${taskId}/updates`,
        { params: { page, per_page: perPage } }
      )
      const body = unwrapData<{ data?: TaskUpdate[] } | TaskUpdate[]>(res)
      const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : []
      return rows.map((row) => mapUpdate(row as unknown as Record<string, unknown>))
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load updates'))
    }
  },

  async createUpdate(taskId: number, payload: TaskUpdateFormValues): Promise<TaskUpdate> {
    try {
      const res = await apiClient.post<ApiResponse<TaskUpdate>>(`/tasks/${taskId}/updates`, payload)
      const data = unwrapData<TaskUpdate | Record<string, unknown>>(res)
      return mapUpdate(data as Record<string, unknown>)
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to add update'))
    }
  },

  async listVisitEvents(taskId: number): Promise<FeVisitEventsResult> {
    try {
      const res = await apiClient.get<ApiResponse<FeVisitEvent[]>>(`/tasks/${taskId}/visit-events`)
      const data = unwrapData<FeVisitEvent[] | Record<string, unknown>[]>(res)
      if (!Array.isArray(data)) {
        return { events: [], endpointMissing: false }
      }
      const events = data
        .map((row) => mapVisitEvent(row as Record<string, unknown>))
        .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      return { events, endpointMissing: false }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { events: [], endpointMissing: true }
      }
      throw new Error(apiErrorMessage(error, 'Failed to load visit events'))
    }
  },

  async listExpenses(taskId: number): Promise<{ expenses: FeExpense[] }> {
    try {
      const res = await apiClient.get<ApiResponse<FeExpense[]>>(`/tasks/${taskId}/expenses`)
      const data = unwrapData<FeExpense[] | Record<string, unknown>[]>(res)
      const expenses = Array.isArray(data)
        ? data.map((row) => ({
            id: Number((row as FeExpense).id),
            child_ticket_id: Number((row as FeExpense).child_ticket_id),
            fe_user_id: Number((row as FeExpense).fe_user_id),
            category: String((row as FeExpense).category ?? ''),
            amount: Number((row as FeExpense).amount ?? 0),
            currency: (row as FeExpense).currency ?? 'PHP',
            status: String((row as FeExpense).status ?? 'submitted'),
            reviewed_by: (row as FeExpense).reviewed_by ?? null,
            reviewed_at: (row as FeExpense).reviewed_at ?? null,
            created_at: (row as FeExpense).created_at ?? null,
          }))
        : []
      return { expenses }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load expenses'))
    }
  },

  async approveExpense(taskId: number, expenseId: number): Promise<void> {
    await apiClient.post(`/tasks/${taskId}/expenses/${expenseId}/approve`)
  },

  async rejectExpense(taskId: number, expenseId: number): Promise<void> {
    await apiClient.post(`/tasks/${taskId}/expenses/${expenseId}/reject`)
  },

  async listChecklist(taskId: number): Promise<TaskChecklistResult> {
    try {
      const res = await apiClient.get<ApiResponse<TaskChecklistResult>>(`/tasks/${taskId}/checklist`)
      const data = unwrapData<TaskChecklistResult>(res)
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        progress: data?.progress ?? {
          total: 0,
          checked: 0,
          required_total: 0,
          required_checked: 0,
        },
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load checklist'))
    }
  },

  async createChecklistItem(
    taskId: number,
    payload: { label: string; required?: boolean },
  ): Promise<TaskChecklistResult> {
    try {
      const res = await apiClient.post<ApiResponse<TaskChecklistResult>>(
        `/tasks/${taskId}/checklist`,
        payload,
      )
      const data = unwrapData<TaskChecklistResult>(res)
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        progress: data?.progress ?? {
          total: 0,
          checked: 0,
          required_total: 0,
          required_checked: 0,
        },
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to add checklist item'))
    }
  },

  async attachChecklistTemplate(
    taskId: number,
    checklistTemplateId: number,
    options?: { replace?: boolean },
  ): Promise<TaskChecklistResult> {
    try {
      const res = await apiClient.post<ApiResponse<TaskChecklistResult>>(
        `/tasks/${taskId}/checklist/attach-template`,
        {
          checklist_template_id: checklistTemplateId,
          replace: Boolean(options?.replace),
        },
      )
      const data = unwrapData<TaskChecklistResult>(res)
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        progress: data?.progress ?? {
          total: 0,
          checked: 0,
          required_total: 0,
          required_checked: 0,
        },
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to attach checklist'))
    }
  },

  async updateChecklistItem(
    taskId: number,
    itemId: number,
    payload: { checked?: boolean; note?: string | null },
  ): Promise<TaskChecklistItem> {
    try {
      const res = await apiClient.patch<ApiResponse<TaskChecklistItem>>(
        `/tasks/${taskId}/checklist/${itemId}`,
        payload,
      )
      return unwrapData<TaskChecklistItem>(res)
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to update checklist item'))
    }
  },

  async deleteChecklistItem(taskId: number, itemId: number): Promise<TaskChecklistResult> {
    try {
      const res = await apiClient.delete<ApiResponse<TaskChecklistResult>>(
        `/tasks/${taskId}/checklist/${itemId}`,
      )
      const data = unwrapData<TaskChecklistResult>(res)
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        progress: data?.progress ?? {
          total: 0,
          checked: 0,
          required_total: 0,
          required_checked: 0,
        },
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to remove checklist item'))
    }
  },

  async listDependencies(taskId: number): Promise<TaskDependenciesResult> {
    try {
      const res = await apiClient.get<ApiResponse<TaskDependenciesResult>>(`/tasks/${taskId}/dependencies`)
      const data = unwrapData<TaskDependenciesResult>(res)
      return {
        blocked_by: Array.isArray(data?.blocked_by) ? data.blocked_by : [],
        blocking: Array.isArray(data?.blocking) ? data.blocking : [],
      }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load dependencies'))
    }
  },

  async listMine(
    page = 1,
    perPage = 25,
  ): Promise<{
    data: ChildTicketTask[]
    meta: { total: number; per_page: number; current_page: number; last_page: number }
  }> {
    try {
      const res = await apiClient.get<
        | ApiResponse<ChildTicketTask[]>
        | { data: ChildTicketTask[]; meta?: Record<string, number> }
      >('/tasks', { params: { scope: 'mine', page, per_page: perPage } })
      const body = res.data as Record<string, unknown>
      const data: ChildTicketTask[] = Array.isArray(body)
        ? (body as ChildTicketTask[])
        : Array.isArray((body as { data?: unknown }).data)
          ? ((body as { data: ChildTicketTask[] }).data)
          : []
      const rawMeta = (body as { meta?: Record<string, number> }).meta
      const meta = {
        total: Number(rawMeta?.total ?? data.length),
        per_page: Number(rawMeta?.per_page ?? perPage),
        current_page: Number(rawMeta?.current_page ?? page),
        last_page: Number(rawMeta?.last_page ?? 1),
      }
      return { data, meta }
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to load tasks'))
    }
  },

  async cancel(taskId: number, reason: string): Promise<ChildTicketTask> {
    try {
      const payload: TaskPatchPayload = { status: 'cancelled' }
      if (reason.trim()) payload.notes = `[Cancel reason]: ${reason.trim()}`
      const res = await apiClient.patch<ApiResponse<ChildTicketTask>>(`/tasks/${taskId}`, payload)
      const data = unwrapData<ChildTicketTask>(res)
      if (!data?.id) throw new Error('Failed to cancel task')
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to cancel task'))
    }
  },

  async reopen(taskId: number, reason?: string): Promise<ChildTicketTask> {
    try {
      const payload: TaskPatchPayload = { status: 'pending' }
      if (reason?.trim()) payload.notes = `[Reopen reason]: ${reason.trim()}`
      const res = await apiClient.patch<ApiResponse<ChildTicketTask>>(`/tasks/${taskId}`, payload)
      const data = unwrapData<ChildTicketTask>(res)
      if (!data?.id) throw new Error('Failed to reopen task')
      return data
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to reopen task'))
    }
  },

  async addDependency(taskId: number, dependsOnTaskId: number): Promise<void> {
    try {
      await apiClient.post(`/tasks/${taskId}/dependencies`, { depends_on_task_id: dependsOnTaskId })
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to add dependency'))
    }
  },

  async removeDependency(taskId: number, dependsOnTaskId: number): Promise<void> {
    try {
      await apiClient.delete(`/tasks/${taskId}/dependencies/${dependsOnTaskId}`)
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'Failed to remove dependency'))
    }
  },
}
